import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes, Global, css } from '@emotion/react';
import { Button, Typography, Flex } from '@robinpowered/ui-kit';
import Close from '@robinpowered/ui-kit-icons/Close';
import { AddSpaceCard } from './MarketingCards/AddSpaceCard';
import { useTranslation } from 'react-i18next';
import { AmplitudeEvents } from '../../constants/amplitudeEvents';
import { useAmplitude } from '../../contexts/AmplitudeContext';

const IOS = 'https://apps.apple.com/us/app/robin-powered/id948856282';
const ANDROID =
  'https://play.google.com/store/apps/details?id=com.robinpowered.compass';

export const DownloadMobileAppModal: FC = () => {
  const [showDownloadRobinAppModal, setShowDownloadRobinAppModal] =
    useState<boolean>(/Android|(iPhone|iPod)/i.test(navigator.userAgent));
  const { t } = useTranslation('downloadMobileAppModal');

  const { trackEvent } = useAmplitude();

  useEffect(() => {
    trackEvent(AmplitudeEvents.SHOW_MOBILE_WEB_TAKEOVER);
  }, [trackEvent]);

  const mobileAppLink = useMemo(() => {
    if (/Android/i.test(navigator.userAgent)) {
      return ANDROID;
    }

    if (/(iPhone|iPod)/i.test(navigator.userAgent)) {
      return IOS;
    }

    return 'https://robin.app/go/mobile';
  }, []);

  const handleDownloadAppAction = (): void => {
    window.location.href = mobileAppLink;
    trackEvent(AmplitudeEvents.SELECT_DOWNLOAD_MOBILE_WEB_TAKEOVER);
    setShowDownloadRobinAppModal(false);
  };

  const handleDismissModal = (): void => {
    setShowDownloadRobinAppModal(false);
    trackEvent(AmplitudeEvents.CLOSE_MOBILE_WEB_TAKEOVER);
  };

  if (!showDownloadRobinAppModal) {
    return null;
  }

  return (
    <>
      <Global styles={globalStyle} />
      <Overlay>
        <ModalScreen>
          <Flex style={{ flexGrow: 0, padding: 16 }}>
            <Button
              onClick={handleDismissModal}
              style={{ marginLeft: 'auto', marginRight: 0 }}
              icon={<Close />}
              type="text"
            />
          </Flex>
          <ContentContainer>
            <CardsContainer>
              <AddSpaceCard />
            </CardsContainer>
            <Typography.Title level={2} style={{ margin: '2rem 0rem 1rem' }}>
              {t('title')}
            </Typography.Title>
            <ListContainer>
              <List>
                <li>
                  <Typography.Paragraph
                    style={{ marginBottom: 0, textAlign: 'left' }}
                  >
                    {t('list_bullet_1')}
                  </Typography.Paragraph>
                </li>
                <li>
                  <Typography.Paragraph
                    style={{ marginBottom: 0, textAlign: 'left' }}
                  >
                    {t('list_bullet_2')}
                  </Typography.Paragraph>
                </li>
                <li>
                  <Typography.Paragraph
                    style={{ marginBottom: 0, textAlign: 'left' }}
                  >
                    {t('list_bullet_3')}
                  </Typography.Paragraph>
                </li>
              </List>
            </ListContainer>
          </ContentContainer>
          <Flex
            vertical
            justify="space-between"
            gap={8}
            style={{ padding: 16 }}
          >
            <Typography.Title level={5}>{t('button_heading')}</Typography.Title>
            <Button
              type="primary"
              onClick={handleDownloadAppAction}
              style={{ height: 44 }}
            >
              {t('button_label')}
            </Button>
          </Flex>
        </ModalScreen>
      </Overlay>
    </>
  );
};

/**
 * Is removed when the component unmounts
 */
const globalStyle = css`
  html,
  body {
    overflow: hidden;
  }
`;

const fadeIn = keyframes`
    from {
       opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const Overlay = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
  background-color: rgba(0, 0, 0, 0.4);
  bottom: 0;
  display: block;
  height: auto;
  left: 0;
  min-height: 100%;
  overflow: hidden;
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  z-index: 1050;
`;

const slideUp = keyframes`
    0% {
        transform: translateY(100%);
        visibility: visible;
    }
    100% {
        transform: translateY(0%);
        visibility: visible;
    }
`;

const ModalScreen = styled.div`
  visibility: hidden;
  background-color: #f8f6f4;
  width: 100vw;
  height: 100vh;
  position: fixed;
  animation: ${slideUp} 0.2s ease-out forwards;
  animation-delay: 0.5s;
  text-align: center;
`;

const ContentContainer = styled.div`
  padding: 0px 16px;
`;

const ListContainer = styled.div`
  padding: 16px 16px 0px;
  margin-left: 8px;
`;

const List = styled.ul`
  list-style: disc;
  color: #2a2a2a;
`;

const CardsContainer = styled.div`
  margin: 10px auto 22px;
  max-width: fit-content;
  padding: 0 24px 24px;
`;
